/**
 * CONSAI Business App - Report View Page
 * Displays generated AI report and task progress
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, FileText, Target, TrendingUp,
  Calendar, CheckCircle2, Circle, Loader2, RefreshCw,
  ChevronDown, ChevronUp, Lightbulb
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportView = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [taskProgress, setTaskProgress] = useState({ completions: [], next_steps: [] });
  const [expandedSection, setExpandedSection] = useState('summary');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchReport();
    fetchTaskProgress();
    
    // Poll for status if processing
    // fetchReport/fetchTaskProgress are defined in this scope; generation_status
    // is the only reactive trigger — functions are stable across re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const interval = setInterval(() => {
      if (report?.generation_status === 'processing' || 
          report?.generation_status === 'generating_report' ||
          report?.generation_status === 'generating_images' ||
          report?.generation_status === 'generating_pdf') {
        fetchReport();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.generation_status]);

  const fetchReport = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/`, {
        credentials: 'include'
      });
      
      if (res.status === 404) {
        // No report yet, redirect to questionnaire
        navigate('/questionnaire');
        return;
      }
      
      const data = await res.json();
      setReport(data);
    } catch (error) {
      toast.error(language === 'hi' ? 'रिपोर्ट लोड करने में त्रुटि' : 'Error loading report');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskProgress = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/tasks/progress`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setTaskProgress(data);
      }
    } catch (error) {
      console.error('Error fetching task progress');
    }
  };

  const handleTaskToggle = async (month, taskText, completed) => {
    try {
      await fetch(`${API_URL}/api/reports/tasks/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ month, task_text: taskText, completed })
      });
      
      // Update local state
      setTaskProgress(prev => {
        const newCompletions = [...prev.completions];
        const existingIdx = newCompletions.findIndex(
          c => c.month === month && c.task_text === taskText
        );
        
        if (existingIdx >= 0) {
          newCompletions[existingIdx] = { ...newCompletions[existingIdx], completed };
        } else {
          newCompletions.push({ month, task_text: taskText, completed });
        }
        
        return { ...prev, completions: newCompletions };
      });
      
      toast.success(completed 
        ? (language === 'hi' ? 'टास्क पूरा!' : 'Task completed!') 
        : (language === 'hi' ? 'टास्क अपडेट किया' : 'Task updated'));
    } catch (error) {
      toast.error(language === 'hi' ? 'अपडेट में त्रुटि' : 'Error updating');
    }
  };

  const isTaskCompleted = (month, taskText) => {
    return taskProgress.completions.some(
      c => c.month === month && c.task_text === taskText && c.completed
    );
  };

  const getCompletionPercentage = () => {
    if (!taskProgress.next_steps?.length) return 0;
    
    let totalTasks = 0;
    let completedTasks = 0;
    
    taskProgress.next_steps.forEach(step => {
      totalTasks += step.tasks?.length || 0;
      step.tasks?.forEach(task => {
        if (isTaskCompleted(step.month, task)) completedTasks++;
      });
    });
    
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const handleDownload = async (version) => {
    try {
      window.open(`${API_URL}/api/reports/${report.report_id}/download/${version}`, '_blank');
    } catch (error) {
      toast.error(language === 'hi' ? 'डाउनलोड में त्रुटि' : 'Error downloading');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchReport();
    await fetchTaskProgress();
    setRefreshing(false);
  };

  const getStatusBadge = () => {
    const status = report?.generation_status;
    if (status === 'completed') {
      return <Badge className="bg-green-100 text-green-700">{language === 'hi' ? 'पूर्ण' : 'Completed'}</Badge>;
    }
    if (status === 'failed') {
      return <Badge className="bg-red-100 text-red-700">{language === 'hi' ? 'विफल' : 'Failed'}</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700">{language === 'hi' ? 'प्रोसेसिंग...' : 'Processing...'}</Badge>;
  };

  const parseReportSections = (text) => {
    if (!text) return [];
    
    const sections = [];
    // BUG FIX 5: More flexible section markers with case-insensitive matching
    const sectionMarkers = [
      { marker: 'EXECUTIVE SUMMARY', title: language === 'hi' ? 'कार्यकारी सारांश' : 'Executive Summary', icon: FileText },
      { marker: 'BUSINESS OVERVIEW', title: language === 'hi' ? 'बिज़नेस ओवरव्यू' : 'Business Overview', icon: Target },
      { marker: 'MARKET CONTEXT', title: language === 'hi' ? 'मार्केट संदर्भ' : 'Market Context', icon: TrendingUp },
      { marker: 'MARKET ANALYSIS', title: language === 'hi' ? 'मार्केट विश्लेषण' : 'Market Analysis', icon: TrendingUp },
      { marker: 'FINANCIAL SNAPSHOT', title: language === 'hi' ? 'फाइनेंशियल स्नैपशॉट' : 'Financial Snapshot', icon: TrendingUp },
      { marker: 'FINANCIAL ANALYSIS', title: language === 'hi' ? 'वित्तीय विश्लेषण' : 'Financial Analysis', icon: TrendingUp },
      { marker: 'OPPORTUNITIES AND CHALLENGES', title: language === 'hi' ? 'अवसर और चुनौतियां' : 'Opportunities & Challenges', icon: Lightbulb },
      { marker: 'OPPORTUNITIES', title: language === 'hi' ? 'अवसर' : 'Opportunities', icon: Lightbulb },
      { marker: 'CHALLENGES', title: language === 'hi' ? 'चुनौतियां' : 'Challenges', icon: Target },
      { marker: 'OPERATIONS AND TECHNOLOGY', title: language === 'hi' ? 'ऑपरेशन्स और टेक्नोलॉजी' : 'Operations & Technology', icon: Target },
      { marker: 'OPERATIONS', title: language === 'hi' ? 'ऑपरेशन्स ऑडिट' : 'Operations Audit', icon: Target },
      { marker: 'TECHNOLOGY AUDIT', title: language === 'hi' ? 'टेक्नोलॉजी ऑडिट' : 'Technology Audit', icon: Target },
      { marker: 'SALES AND MARKETING', title: language === 'hi' ? 'सेल्स और मार्केटिंग' : 'Sales & Marketing', icon: TrendingUp },
      { marker: 'MARKETING STRATEGY', title: language === 'hi' ? 'मार्केटिंग रणनीति' : 'Marketing Strategy', icon: TrendingUp },
      { marker: 'STRATEGIC RECOMMENDATIONS', title: language === 'hi' ? 'स्ट्रैटेजिक सिफारिशें' : 'Strategic Recommendations', icon: Lightbulb },
      { marker: 'RECOMMENDATIONS', title: language === 'hi' ? 'सिफारिशें' : 'Recommendations', icon: Lightbulb },
      { marker: 'NEXT STEPS', title: language === 'hi' ? 'अगले कदम' : 'Next Steps', icon: Calendar },
      { marker: '90-DAY ACTION PLAN', title: language === 'hi' ? '90-दिन एक्शन प्लान' : '90-Day Action Plan', icon: Calendar },
      { marker: 'ACTION PLAN', title: language === 'hi' ? 'एक्शन प्लान' : 'Action Plan', icon: Calendar },
      { marker: 'SIMPLE SUMMARY', title: language === 'hi' ? 'सरल सारांश' : 'Simple Summary', icon: FileText },
      { marker: 'CONCLUSION', title: language === 'hi' ? 'निष्कर्ष' : 'Conclusion', icon: FileText }
    ];
    
    let currentSection = null;
    let currentContent = [];
    
    text.split('\n').forEach(line => {
      const lineUpper = line.toUpperCase().trim();
      // Check for markdown headers (# or ##) and section markers
      const cleanLine = lineUpper.replace(/^#+\s*/, '').replace(/[*_]/g, '');
      const foundMarker = sectionMarkers.find(s => cleanLine.includes(s.marker));
      if (foundMarker) {
        if (currentSection && currentContent.length > 0) {
          sections.push({ ...currentSection, content: currentContent.join('\n').trim() });
        }
        currentSection = { title: foundMarker.title, icon: foundMarker.icon };
        currentContent = [];
      } else if (currentSection) {
        currentContent.push(line);
      }
    });
    
    // Save last section
    if (currentSection && currentContent.length > 0) {
      sections.push({ ...currentSection, content: currentContent.join('\n').trim() });
    }
    
    // If no sections found, create a single section with all content
    if (sections.length === 0 && text.trim()) {
      sections.push({
        title: language === 'hi' ? 'रिपोर्ट' : 'Report',
        icon: FileText,
        content: text.trim()
      });
    }
    
    return sections;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#021945]" />
      </div>
    );
  }

  // Show processing state
  if (report && report.generation_status !== 'completed' && report.generation_status !== 'failed') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar />
        <div className="lg:ml-64 pb-20 lg:pb-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
            <div className="px-4 md:px-8 py-4">
              <h1 className="text-xl font-bold font-manrope text-slate-900">
                {language === 'hi' ? 'रिपोर्ट जनरेट हो रही है' : 'Generating Report'}
              </h1>
            </div>
          </header>
          <main className="p-4 md:p-8">
            <Card className="max-w-xl mx-auto">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#021945]/10 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="h-8 w-8 animate-spin text-[#021945]" />
                </div>
                <h2 className="text-xl font-bold font-manrope text-slate-900 mb-2">
                  {report.generation_status === 'generating_report' && (language === 'hi' ? 'AI रिपोर्ट लिख रहा है...' : 'AI is writing your report...')}
                  {report.generation_status === 'generating_images' && (language === 'hi' ? 'इमेज जनरेट हो रही हैं...' : 'Generating images...')}
                  {report.generation_status === 'generating_pdf' && (language === 'hi' ? 'PDF बन रही है...' : 'Creating PDF...')}
                  {report.generation_status === 'processing' && (language === 'hi' ? 'प्रोसेसिंग...' : 'Processing...')}
                </h2>
                <p className="text-slate-500">
                  {language === 'hi' 
                    ? 'इसमें 2-3 मिनट लग सकते हैं' 
                    : 'This may take 2-3 minutes'}
                </p>
              </CardContent>
            </Card>
          </main>
        </div>
        <MobileNav />
      </div>
    );
  }

  const reportSections = parseReportSections(report?.full_report_text);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      
      <div className="lg:ml-64 pb-20 lg:pb-8">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold font-manrope text-slate-900">
                {language === 'hi' ? 'AI बिज़नेस रिपोर्ट' : 'AI Business Report'}
              </h1>
              {getStatusBadge()}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              {report?.generation_status === 'completed' && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload('text')}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {language === 'hi' ? 'टेक्स्ट PDF' : 'Text PDF'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDownload('rich')}
                    className="bg-[#d7263d] hover:bg-[#d7263d]/90"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {language === 'hi' ? 'फुल PDF' : 'Full PDF'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="p-4 md:p-8">
          {report?.generation_status === 'failed' ? (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6">
                <p className="text-red-700">
                  {language === 'hi' 
                    ? 'रिपोर्ट जनरेशन विफल। कृपया दोबारा प्रयास करें।' 
                    : 'Report generation failed. Please try again.'}
                </p>
                {report.error_message && (
                  <p className="text-red-600 text-sm mt-2">{report.error_message}</p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Tabs defaultValue="report" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
                <TabsTrigger value="report" className="">
                  <FileText className="h-4 w-4 mr-2" />
                  {language === 'hi' ? 'रिपोर्ट' : 'Report'}
                </TabsTrigger>
                <TabsTrigger value="tasks" className="">
                  <Calendar className="h-4 w-4 mr-2" />
                  {language === 'hi' ? 'एक्शन प्लान' : 'Action Plan'}
                </TabsTrigger>
              </TabsList>

              {/* Report Tab */}
              <TabsContent value="report" className="space-y-4">
                {/* KPIs */}
                {report?.kpis?.length > 0 && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {report.kpis.map((kpi, i) => (
                      <Card key={kpi.name || `kpi-${i}`}>
                        <CardContent className="pt-4 pb-4">
                          <p className="text-2xl font-bold text-[#021945] font-manrope">{kpi.target}</p>
                          <p className="text-sm text-slate-600">{kpi.name}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Report Sections */}
                {reportSections.map((section, index) => (
                  <Card key={section.title || `section-${index}`}>
                    <CardHeader 
                      className="cursor-pointer"
                      onClick={() => setExpandedSection(expandedSection === `section-${index}` ? '' : `section-${index}`)}
                    >
                      <div className="flex items-center justify-between">
                        <CardTitle className="font-manrope flex items-center gap-2 text-base">
                          <section.icon className="h-5 w-5 text-[#021945]" />
                          {section.title}
                        </CardTitle>
                        {expandedSection === `section-${index}` ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </CardHeader>
                    {expandedSection === `section-${index}` && (
                      <CardContent>
                        <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
                          {section.content}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}

                {/* Recommendations */}
                {report?.recommendations?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-manrope flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-amber-500" />
                        {language === 'hi' ? 'मुख्य सिफारिशें' : 'Key Recommendations'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {report.recommendations.map((rec, i) => (
                          <div key={rec.title || `rec-${i}`} className="flex gap-3">
                            <Badge className={`h-fit ${
                              rec.priority === 'high' ? 'bg-red-100 text-red-700' : 
                              rec.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 
                              'bg-green-100 text-green-700'
                            }`}>
                              {rec.priority === 'high' ? '!' : rec.priority === 'medium' ? '!!' : '!!!'}
                            </Badge>
                            <div>
                              <p className="font-medium text-slate-900">{rec.title}</p>
                              <p className="text-sm text-slate-600">{rec.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Tasks Tab */}
              <TabsContent value="tasks" className="space-y-6">
                {/* Progress Overview */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-medium font-manrope">
                        {language === 'hi' ? 'कुल प्रगति' : 'Overall Progress'}
                      </span>
                      <span className="text-2xl font-bold text-[#021945]">{getCompletionPercentage()}%</span>
                    </div>
                    <Progress value={getCompletionPercentage()} className="h-3" />
                  </CardContent>
                </Card>

                {/* Monthly Tasks */}
                {(taskProgress.next_steps?.length > 0 ? taskProgress.next_steps : report?.next_steps)?.map((step, monthIndex) => (
                  <Card key={monthIndex}>
                    <CardHeader>
                      <CardTitle className="font-manrope flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-[#021945]" />
                        {language === 'hi' ? `महीना ${step.month}` : `Month ${step.month}`}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {step.tasks?.map((task, taskIndex) => {
                          const completed = isTaskCompleted(step.month, task);
                          return (
                            <div 
                              key={taskIndex} 
                              className={`flex items-start gap-3 p-3 rounded-lg transition-all ${
                                completed ? 'bg-green-50' : 'bg-slate-50'
                              }`}
                            >
                              <Checkbox
                                checked={completed}
                                onCheckedChange={(checked) => handleTaskToggle(step.month, task, checked)}
                                className="mt-0.5"
                              />
                              <span className={`${completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                                {task}
                              </span>
                              {completed && <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>
            </Tabs>
          )}
        </main>
      </div>

      <MobileNav />
    </div>
  );
};

export default ReportView;
